import { useState, useEffect } from 'react'
import { Box, Container, Flex, Button, Icon, useToast } from '@chakra-ui/react'
import { PageTransition } from '../../../components/PageTransition'
import { HeaderSection } from '../HeaderSection'
import { usePedidos } from './hooks/usePedidos'
import { FilterForm } from './components/FilterForm'
import { ResultsSummary } from './components/ResultsSummary'
import { PedidosTable } from './components/PedidosTable'
import { StatusLegend } from './components/StatusLegend'
import { FormProvider, useForm } from 'react-hook-form'
import { Link, useSearchParams } from 'react-router-dom'
import { FiArrowLeft, FiRefreshCw } from 'react-icons/fi'

export function AcompanharPedido() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [filtrosAtivos, setFiltrosAtivos] = useState({})
  const toast = useToast()

  const defaultValues = {
    numeroPedido: '',
    dataInicio: '',
    dataFim: '',
    passoAtual: '',
    proximoPasso: '',
    tiposGravacao: {
      laser: false,
      serigrafia: false,
      tampografia: false,
      uv: false,
      silk: false,
      etiqueta: false,
      placa: false,
      dtf: false,
      colorida: false,
    },
  }

  const methods = useForm({
    defaultValues: defaultValues,
  })

  useEffect(() => {
    const filtrosFromUrl = {
      numeroPedido: searchParams.get('numeroPedido') || '',
      dataInicio: searchParams.get('dataInicio') || '',
      dataFim: searchParams.get('dataFim') || '',
      passoAtual: searchParams.get('passoAtual') || '',
      proximoPasso: searchParams.get('proximoPasso') || '',
      tiposGravacao: {
        laser: searchParams.get('laser') === 'true',
        serigrafia: searchParams.get('serigrafia') === 'true',
        tampografia: searchParams.get('tampografia') === 'true',
        uv: searchParams.get('uv') === 'true',
        silk: searchParams.get('silk') === 'true',
        etiqueta: searchParams.get('etiqueta') === 'true',
        placa: searchParams.get('placa') === 'true',
        dtf: searchParams.get('dtf') === 'true',
        colorida: searchParams.get('colorida') === 'true',
      },
    }

    methods.reset(filtrosFromUrl)

    const tiposGravacaoSelecionados = Object.entries(filtrosFromUrl.tiposGravacao)
      .filter(([_, value]) => value === true)
      .map(([key]) => key)

    if (searchParams.toString()) {
      setFiltrosAtivos({
        ...filtrosFromUrl,
        tiposGravacao: tiposGravacaoSelecionados,
      })
    }
  }, [])

  const { data: pedidos = [], isLoading, error, refetch, isFetching, isRefetching } = usePedidos(filtrosAtivos)

  const handleFilter = (data) => {
    console.log('Filtros aplicados:', data)

    const tiposGravacaoSelecionados = Object.entries(data.tiposGravacao)
      .filter(([_, value]) => value === true)
      .map(([key]) => key)

    const filtros = {
      ...data,
      tiposGravacao: tiposGravacaoSelecionados,
    }

    setFiltrosAtivos(filtros)

    const urlParams = new URLSearchParams()

    if (data.numeroPedido) urlParams.set('numeroPedido', data.numeroPedido)
    if (data.dataInicio) urlParams.set('dataInicio', data.dataInicio)
    if (data.dataFim) urlParams.set('dataFim', data.dataFim)
    if (data.passoAtual) urlParams.set('passoAtual', data.passoAtual)
    if (data.proximoPasso) urlParams.set('proximoPasso', data.proximoPasso)

    Object.entries(data.tiposGravacao).forEach(([key, value]) => {
      if (value) urlParams.set(key, 'true')
    })

    setSearchParams(urlParams)

    toast({
      title: 'Filtros aplicados',
      description: 'Buscando pedidos com os filtros selecionados',
      status: 'info',
      duration: 2000,
      isClosable: true,
    })
  }

  const handleClearFilters = () => {
    document.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      checkbox.checked = false
    })

    methods.reset(defaultValues, {
      keepDirty: false,
      keepErrors: false,
      keepIsSubmitted: false,
      keepTouched: false,
      keepIsValid: false,
      keepSubmitCount: false,
    })

    setFiltrosAtivos({})

    setSearchParams({})

    toast({
      title: 'Filtros limpos',
      description: 'Todos os filtros foram removidos',
      status: 'info',
      duration: 2000,
      isClosable: true,
    })

    setTimeout(() => {
      refetch()
    }, 0)
  }

  const handleRefresh = () => {
    refetch()

    toast({
      title: 'Atualizando dados',
      description: 'Buscando os dados mais recentes',
      status: 'info',
      duration: 2000,
      isClosable: true,
    })
  }

  const totalPecas = pedidos.reduce((sum, p) => {
    const quantidadeStr = typeof p.quantidade === 'string' ? p.quantidade.replace(/\./g, '').replace(/\,/g, '.') : String(p.quantidade)
    return sum + (parseFloat(quantidadeStr) || 0)
  }, 0)

  return (
    <PageTransition>
      <Box bg="white" minHeight="100vh">
        <HeaderSection title="STATUS DOS MEUS PEDIDOS" />

        <Container maxW="1366px" px={2} py={6} overflow="visible">
          <FormProvider {...methods}>
            {/* Componente de filtros com checkboxes */}
            <FilterForm onSubmit={handleFilter} onClear={handleClearFilters} isSubmitting={isLoading || isFetching} />

            {/* Componente de resumo de resultados com botão de atualização e legenda */}
            <Flex justifyContent="space-between" alignItems="center" mb={4}>
              <ResultsSummary totalPedidos={pedidos.length} totalItens={pedidos.length} totalPecas={totalPecas} />

              <Flex alignItems="center">
                <StatusLegend />
                <Button
                  size="sm"
                  leftIcon={<Icon as={FiRefreshCw} />}
                  colorScheme="blue"
                  variant="outline"
                  onClick={handleRefresh}
                  isLoading={isRefetching}
                  loadingText="Atualizando"
                  disabled={isLoading || isFetching}
                  fontSize="12px"
                >
                  Atualizar
                </Button>
              </Flex>
            </Flex>

            {/* Componente da tabela de pedidos */}
            <PedidosTable pedidos={pedidos} isLoading={isLoading} isFetching={isFetching} isRefetching={isRefetching} error={error} />

            {/* Botão voltar */}
            <Flex mt={8} mb={10} justifyContent="center">
              <Button
                leftIcon={<Icon as={FiArrowLeft} />}
                variant="ghost"
                as={Link}
                to="/omnipromo"
                color="#313E44"
                fontWeight="normal"
                fontSize="12px"
              >
                Voltar
              </Button>
            </Flex>
          </FormProvider>
        </Container>
      </Box>
    </PageTransition>
  )
}
